import {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {Avatar, Box, Chip, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, IconButton, MenuItem, Radio, RadioGroup, TextField, Tooltip, Typography} from "@mui/material";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import UnfoldLessOutlinedIcon from "@mui/icons-material/UnfoldLessOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {goldErrorFieldSx, useStyles} from "../../utils/uiUtils";
import {emailValidationRules, firstNameValidationRules, lastNameValidationRules, nationalPhoneNumberValidationRules} from "../../utils/validationRules";
import {applyNationalInput, DIAL_CODES, joinPhoneNumber, splitPhoneNumber} from "../../utils/phoneNumbers";
import {SIGN_IN_METHOD_HINT, SIGN_IN_METHOD_OPTIONS, TITLE_OPTIONS} from "../CreateEditDialog/dialogBoxConfig";

/**
 * UserBadge — renders a user as a compact chip badge or expanded editable fields.
 *
 * Props:
 *   value          — full user object { first_name, last_name, email, phone_number, mobile_number, role, profile_picture }
 *                    OR an email string (resolved async via resolveUserByEmail)
 *   onChange       — (userObject) => void — called on field change when expanded
 *   onSave         — () => void — called when Enter pressed in any field
 *   label          — optional label shown left of badge / above expanded fields
 *   fields         — subset of user fields to show expanded. Defaults to all editable fields except
 *                    entra_login, which a caller has to ask for by listing it.
 *   canToggle      — if true, shows expand/collapse button (default false)
 *   active         — if false, fields are read-only when expanded (default true)
 *   actions        — React node injected into the label row (for UserPicker button)
 *   size           — "sm" (28px avatar) | "md" (40px avatar, default)
 *   requiredFields — fields that must be filled when editing. Defaults to ['email', 'mobile_number'].
 *                    Pass [] to disable all required validation.
 *
 * Phone and Mobile are "dialled" fields: a country-code dropdown beside a number box, editing the one
 * string the API stores. The value handed in and pushed back out is unchanged — "+61 400 111 222", not a
 * pair — so no consumer of this component needs to know. See utils/phoneNumbers.js for the split.
 */

/*
 * entra_login (Sign-in Method) is in the layout order, on its own row below Last Name, but not in the default set. It is a
 * decision about how a new portal user gets in, so it belongs only where the badge registers one - the
 * Primary Contact on Account Creation - and not on a shipping or job contact, which is just a name and number.
 */
const FIELD_ORDER = ["title", "first_name", "middle_name", "last_name", "entra_login", "email", "phone_number", "mobile_number"];
const ALL_FIELDS = FIELD_ORDER.filter(f => f !== "entra_login");
// Every field, Sign-in Method included: what a badge that registers a portal user passes as `fields`.
export const REGISTRATION_FIELDS = FIELD_ORDER;

const TITLE_DISPLAY = Object.fromEntries(TITLE_OPTIONS.map(t => [t.value, t.label]));

const FIELD_CONFIG = {
    title:         {label: "Salutation",    xs: 3,  rules: null,                     options: TITLE_OPTIONS.map(t => t.value), optionLabels: TITLE_DISPLAY},
    first_name:    {label: "First Name",    xs: 4,  rules: firstNameValidationRules},
    middle_name:   {label: "Middle Name",   xs: 5,  rules: null},
    last_name:     {label: "Last Name",     xs: 6,  rules: lastNameValidationRules},
    entra_login:   {label: "Sign-in Method", xs: 12, rules: null, radio: SIGN_IN_METHOD_OPTIONS},
    email:         {label: "Email",         xs: 12, rules: emailValidationRules,      required: true},
    phone_number:  {label: "Phone",         xs: 6,  rules: nationalPhoneNumberValidationRules, dialled: true},
    mobile_number: {label: "Mobile",        xs: 6,  rules: nationalPhoneNumberValidationRules, dialled: true, required: true},
};

const DEFAULT_REQUIRED_FIELDS = Object.entries(FIELD_CONFIG)
    .filter(([, cfg]) => cfg.required)
    .map(([field]) => field);  // ['email', 'mobile_number']

const validateField = (field, value, isRequired) => {
    const cfg = FIELD_CONFIG[field] || {};
    // A dialled field stores code and number as one string, but only the number half is the operator's
    // to get wrong — the code came from a fixed list. So both the required check and the format check
    // look at the national part: "+61" alone is a selected country, not a filled-in Mobile.
    const checked = cfg.dialled ? splitPhoneNumber(value).national : value;
    if (isRequired && !checked?.trim()) return `${cfg.label} is required`;
    if (!cfg.rules || !checked) return null;
    if (cfg.rules.pattern && !cfg.rules.pattern.value.test(checked)) return cfg.rules.pattern.message;
    return null;
};

const formatBadge = (user) => {
    if (!user) return "—";
    const parts = [
        user.title ? (TITLE_DISPLAY[user.title] || user.title) : null,
        user.first_name, user.middle_name, user.last_name
    ].filter(Boolean);
    const name = parts.join(' ').trim();
    return name || user.email || "—";
};

const TooltipContent = ({user}) => (
    <Box sx={{p: 0.5}}>
        <Typography sx={{fontSize: 13, fontWeight: 600}}>{formatBadge(user)}</Typography>
        {user.email && <Typography sx={{fontSize: 12, color: '#BABABA'}}>Email: {user.email}</Typography>}
        {user.phone_number && <Typography sx={{fontSize: 12, color: '#BABABA'}}>{user.phone_number}</Typography>}
        {user.mobile_number && <Typography sx={{fontSize: 12, color: '#BABABA'}}>{user.mobile_number}</Typography>}
        {user.role && <Typography sx={{fontSize: 12, color: '#FFC540'}}>{user.role}</Typography>}
    </Box>
);

const UserAvatar = ({user, size}) => {
    const avatarSize = size === "sm" ? 28 : 40;
    const pictureUrl = user?.profile_picture_url || user?.profile_picture || '';

    return (
        <Avatar src={pictureUrl} sx={{
            width: avatarSize, height: avatarSize,
            bgcolor: "rgba(255,197,64,0.15)",
            border: "1px solid rgba(255,197,64,0.4)",
        }}>
            {!pictureUrl && <PersonOutlinedIcon sx={{fontSize: avatarSize * 0.55, color: "#FFC540"}}/>}
        </Avatar>
    );
};

export const UserBadge = forwardRef(({
    value,
    onChange,
    onSave,
    label,
    fields = ALL_FIELDS,
    canToggle = false,
    active = true,
    actions,
    onDelete,
    size = "md",
    requiredFields = DEFAULT_REQUIRED_FIELDS,
}, ref) => {
    const classes = useStyles();
    const [resolvedUser, setResolvedUser] = useState(null);
    const [resolving, setResolving] = useState(false);

    // Determine if value is an email string or a full object
    const isEmailOnly = typeof value === 'string';
    const user = isEmailOnly ? resolvedUser : value;
    const empty = !value;  // based on prop, not resolved state

    const [expanded, setExpanded] = useState(!value);
    const [localValues, setLocalValues] = useState(user || {});
    const [localErrors, setLocalErrors] = useState({});
    const [touched, setTouched] = useState(new Set());
    // Country code per dialled field. Not derivable from localValues alone: picking +1 before typing any
    // digits stores nothing (a code on its own is not a number), so without this the select would snap
    // back to the default on the very next render and the operator's choice would be lost.
    const [dialCodes, setDialCodes] = useState({});
    const visibleFields = FIELD_ORDER.filter(f => fields.includes(f));

    // Expose validate() and flush() so parent components can imperatively trigger
    // validation or push local edits upstream without the operator pressing Enter.
    useImperativeHandle(ref, () => ({
        validate: () => {
            const allTouched = new Set(visibleFields);
            setTouched(allTouched);
            const errors = {};
            visibleFields.forEach(f => {
                errors[f] = validateField(f, localValues[f], requiredFields.includes(f));
            });
            setLocalErrors(errors);
            const isValid = !Object.values(errors).some(Boolean);
            if (!isValid) setExpanded(true);
            return isValid;
        },
        // Push local edits upstream via onChange without collapsing the badge.
        // Call before an external submit button so the parent form receives the
        // current local values even if the operator never pressed Enter inside the badge.
        flush: () => {
            if (touched.size > 0 && onChange) onChange(localValues);
        },
    }));

    // Sync from external value only (dialog pick, async resolve).
    // Internal edits do NOT call onChange — the value prop never bounces back —
    // so this effect only fires on genuine external updates.
    useEffect(() => {
        if (!user) return;
        setLocalValues(user);
        setLocalErrors({});
        setTouched(new Set());
        setDialCodes({});
    }, [JSON.stringify(user)]);

    // Async resolution when value is an email string
    useEffect(() => {
        if (!isEmailOnly || !value) return;
        setResolving(true);
        import('../../utils/apiCalls').then(({resolveUserByEmail}) => {
            resolveUserByEmail(value)
                .then(match => { setResolvedUser(match || null); })
                .catch(() => setResolvedUser(null))
                .finally(() => setResolving(false));
        });
    }, [value, isEmailOnly]);

    const handleCollapse = () => {
        setExpanded(false);
        setLocalValues(user || {});
        setLocalErrors({});
        setTouched(new Set());
        setDialCodes({});
    };

    const handleFieldChange = (field, fieldValue) => {
        setTouched(prev => new Set(prev).add(field));
        setLocalValues(prev => ({...prev, [field]: fieldValue}));
    };

    /* ── Dialled fields (Phone, Mobile): two boxes editing one stored string ──
     *
     * The code selected wins over the code read back out of the stored value, because there are moments
     * when the stored value has no code to read: a blank number stores blank whatever is selected.
     */
    const dialOf = (field) => dialCodes[field] || splitPhoneNumber(localValues[field]).dial;

    const handleDialChange = (field, dial) => {
        setDialCodes(prev => ({...prev, [field]: dial}));
        handleFieldChange(field, joinPhoneNumber(dial, splitPhoneNumber(localValues[field]).national));
    };

    const handleNationalChange = (field, typed) => {
        // applyNationalInput moves a code out of the number box and into the select when it recognises
        // one, so a number pasted whole from an email lands in the right two places.
        const {dial, national} = applyNationalInput(typed, dialOf(field));
        setDialCodes(prev => ({...prev, [field]: dial}));
        handleFieldChange(field, joinPhoneNumber(dial, national));
    };

    const handleFieldBlur = (field, fieldValue) => {
        if (touched.size === 0) return;
        const errors = {...localErrors};
        // Once any field is touched, validate all required visible fields on every blur
        visibleFields.forEach(f => {
            if (requiredFields.includes(f)) {
                // Use fresh event value for the field being blurred to avoid stale state
                const val = f === field ? fieldValue : localValues[f];
                errors[f] = validateField(f, val, true);
            }
        });
        // Also validate format of the blurred field if it is non-required
        if (!requiredFields.includes(field)) {
            errors[field] = validateField(field, fieldValue, false);
        }
        setLocalErrors(errors);
    };

    // Validate all touched fields synchronously from current localValues.
    // Used by the Enter handler where localErrors state may be stale
    // (field was just edited without blurring first).
    const computeCurrentErrors = (currentValues, currentTouched) => {
        const errors = {...localErrors};
        currentTouched.forEach(field => {
            errors[field] = validateField(field, currentValues[field], requiredFields.includes(field));
        });
        return errors;
    };

    // Enter commits the badge from any of its boxes, including either half of a dialled field.
    const handleEnter = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        const currentErrors = computeCurrentErrors(localValues, touched);
        setLocalErrors(currentErrors);
        if (Object.values(currentErrors).some(Boolean)) return;
        if (onChange) onChange(localValues);
        if (onSave) onSave();
        handleCollapse();
    };

    const avatarSize = size === "sm" ? 28 : 40;

    const deleteButton = onDelete ? (
        <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            sx={{p: 0.25, pointerEvents: 'auto', color: '#BABABA', '&:hover': {color: '#FFC540'}}}
        >
            <DeleteOutlineIcon sx={{fontSize: 16}}/>
        </IconButton>
    ) : null;

    const actionsWithDelete = (actions || deleteButton)
        ? <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>{actions}{deleteButton}</Box>
        : null;
    const fontSize = size === "sm" ? 12 : 14;

    if (resolving) {
        return <Typography sx={{fontSize, color: '#BABABA', fontFamily: 'Poppins'}}>Resolving...</Typography>;
    }

    return (
        <Box>
            {!expanded ? (
                /* ── Collapsed chip ── */
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    {label && (
                        <Typography sx={{...classes.labelMuted, minWidth: '140px', textAlign: 'left', color: '#BABABA !important'}}>{label}</Typography>
                    )}
                    <Tooltip title={user ? <TooltipContent user={user}/> : ""} placement="top" arrow>
                        <Chip
                            avatar={<UserAvatar user={user} size={size}/>}
                            label={
                                <Typography sx={{...classes.textWhite, fontSize, opacity: empty ? 0.4 : 1}}>
                                    {resolving ? 'Resolving...' : formatBadge(user)}
                                </Typography>
                            }
                            deleteIcon={
                                canToggle
                                    ? <UnfoldMoreOutlinedIcon sx={{fontSize: 16, color: "#FFFFFF !important", "&:hover": {color: "#FFC540 !important"}}}/>
                                    : deleteButton
                                        ? <DeleteOutlineIcon sx={{fontSize: 16, color: "#BABABA !important", "&:hover": {color: "#FFC540 !important"}}}/>
                                        : undefined
                            }
                            onDelete={
                                canToggle ? () => setExpanded(true) :
                                deleteButton ? (e) => { e.stopPropagation(); onDelete(); } :
                                undefined
                            }
                            variant="outlined"
                            sx={{
                                pointerEvents: 'auto',
                                height: avatarSize + 8,
                                borderRadius: `${(avatarSize + 8) / 2}px`,
                                borderColor: "rgba(255,255,255,0.2)",
                                backgroundColor: "rgba(255,255,255,0.04)",
                                paddingLeft: "4px",
                                "& .MuiChip-avatar": {marginLeft: "0 !important", marginRight: "0 !important"},
                                "& .MuiChip-label": {px: 1.5, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'},
                                "& .MuiChip-label *": {color: "#FFFFFF !important"},
                                "&:hover": {borderColor: empty ? "rgba(255,255,255,0.2)" : "rgba(255,197,64,0.4)"},
                            }}
                        />
                    </Tooltip>
                    {actions}
                </Box>
            ) : (
                /* ── Expanded fields ── */
                <Box>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            {label && (
                                <Typography sx={{...classes.labelMuted, textAlign: 'left', color: '#BABABA !important'}}>{label}</Typography>
                            )}
                            {actions}
                        </Box>
                        <IconButton size="small" onClick={handleCollapse} sx={{pointerEvents: 'auto', p: 0.25}}>
                            <UnfoldLessOutlinedIcon sx={{fontSize: 16, color: "#FFC540"}}/>
                            <Typography sx={{color: "#FFC540", fontFamily: "Poppins", fontSize: 12, ml: 0.5}}>Collapse</Typography>
                        </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                        {visibleFields.map(field => {
                            const cfg = FIELD_CONFIG[field];
                            const isRequired = requiredFields.includes(field);
                            const fieldLabel = `${cfg.label}${isRequired && active ? ' *' : ''}`;
                            const hasError = Boolean(localErrors[field]);
                            return (
                                <Grid item xs={cfg.xs} key={field}>
                                    {cfg.radio ? (
                                        /* A choice between two named paths, so a radio and not a switch - the
                                         * same reasoning as the radioInput type on Add New User. The value is a
                                         * boolean and a radio carries a string, so it goes through String() on
                                         * the way out and is looked back up in the options on the way in;
                                         * anything else would hand the form "false", which reads as true. A
                                         * contact with no answer yet shows the password option, which is the
                                         * Add New User default and the path that works for every company. */
                                        <FormControl component="fieldset" variant="standard" disabled={!active} sx={{width: "100%", textAlign: "left"}}>
                                            <FormLabel
                                                component="legend"
                                                sx={{color: "#FFFFFF", fontSize: 14, mb: 0.5, textAlign: "left", "&.Mui-focused": {color: "#FFFFFF"}}}
                                            >
                                                {cfg.label}
                                            </FormLabel>
                                            <RadioGroup
                                                row
                                                value={String(localValues[field] === true)}
                                                onChange={(e) => {
                                                    if (!active) return;
                                                    const picked = cfg.radio.find(o => String(o.value) === e.target.value);
                                                    handleFieldChange(field, picked ? picked.value : false);
                                                }}
                                            >
                                                {cfg.radio.map(option => (
                                                    <FormControlLabel
                                                        key={String(option.value)}
                                                        value={String(option.value)}
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                sx={{color: "rgba(255,255,255,0.7)", "&.Mui-checked": {color: "#FFC540"}}}
                                                            />
                                                        }
                                                        label={<Typography sx={{color: "#FFFFFF", fontSize: 14}}>{option.label}</Typography>}
                                                    />
                                                ))}
                                            </RadioGroup>
                                            <Typography sx={{color: "rgba(255,255,255,0.5)", fontSize: 11, fontStyle: "italic", textAlign: "left"}}>
                                                *{SIGN_IN_METHOD_HINT}
                                            </Typography>
                                        </FormControl>
                                    ) : cfg.dialled ? (
                                        /* Country code and number: two boxes, one stored string. The code is
                                         * picked, never typed, which is what lets the format check on the
                                         * number be about the number alone. */
                                        <Box sx={{display: 'flex', gap: 1}}>
                                            <TextField
                                                /* "Country code", not "Phone code"/"Mobile code": what the box
                                                 * holds is the country, and the field it belongs to is already
                                                 * named by the box beside it. Both dialled fields on this row
                                                 * therefore carry the same label, so tests reach them by testid
                                                 * rather than by name. */
                                                label="Country code"
                                                data-testid={`${field}-code`}
                                                size="small"
                                                select
                                                value={dialOf(field)}
                                                onChange={(e) => active && handleDialChange(field, e.target.value)}
                                                onKeyDown={handleEnter}
                                                className={classes.selectLabelGreyDiff}
                                                InputLabelProps={{shrink: true}}
                                                // Closed, the box shows just "+61"; the country name is only
                                                // needed while choosing, and would not fit here anyway.
                                                SelectProps={{renderValue: (selected) => selected}}
                                                sx={{flex: '0 0 auto', width: 124}}
                                                disabled={!active}
                                            >
                                                {/* Country first: the list is sorted by country and long
                                                  * enough that a leading code reads as unsorted, and MUI's
                                                  * type-ahead matches the start of the text. */}
                                                {DIAL_CODES.map(({dial, label: country}) => (
                                                    <MenuItem key={dial} value={dial}>{`${country}  ${dial}`}</MenuItem>
                                                ))}
                                            </TextField>
                                            {/* The number box and its message share a column, so "Mobile is
                                              * required" sits under the box it is about. Left at the row level
                                              * it started under the Country code dropdown instead, reading as
                                              * a complaint about the one box on the row that cannot be wrong —
                                              * it is picked from a list and always has a value. */}
                                            <Box sx={{flex: 1, minWidth: 0}} data-testid={`${field}-number`}>
                                                <TextField
                                                    label={fieldLabel}
                                                    size="small"
                                                    fullWidth
                                                    value={splitPhoneNumber(localValues[field]).national}
                                                    onChange={(e) => active && handleNationalChange(field, e.target.value)}
                                                    // Blur validates the stored string, not the box, so the joined
                                                    // value goes in — same shape validateField expects everywhere.
                                                    onBlur={(e) => active && handleFieldBlur(field, joinPhoneNumber(dialOf(field), e.target.value))}
                                                    onKeyDown={handleEnter}
                                                    className={classes.selectLabelGreyDiff}
                                                    sx={hasError ? goldErrorFieldSx : {}}
                                                    InputLabelProps={{shrink: true}}
                                                    disabled={!active}
                                                    error={false}
                                                />
                                                {hasError && (
                                                    <FormHelperText sx={{mx: 0, color: '#FFC540'}}>{localErrors[field]}</FormHelperText>
                                                )}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <TextField
                                            label={fieldLabel}
                                            size="small"
                                            fullWidth
                                            select={Boolean(cfg.options)}
                                            value={localValues[field] || ""}
                                            onChange={(e) => active && handleFieldChange(field, e.target.value)}
                                            onBlur={(e) => active && handleFieldBlur(field, e.target.value)}
                                            onKeyDown={handleEnter}
                                            className={classes.selectLabelGreyDiff}
                                            sx={hasError ? goldErrorFieldSx : {}}
                                            InputLabelProps={{shrink: true}}
                                            disabled={!active}
                                            error={false}
                                        >
                                            {cfg.options?.map(opt => (
                                                <MenuItem key={opt} value={opt}>
                                                    {cfg.optionLabels?.[opt] || opt}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                    {/* Dialled fields carry their own, beside the code box rather than under it. */}
                                    {hasError && !cfg.dialled && (
                                        <FormHelperText sx={{mx: 0, color: '#FFC540'}}>{localErrors[field]}</FormHelperText>
                                    )}
                                </Grid>
                            );
                        })}
                        {/* Role — display-only when present */}
                        {user?.role && !fields.includes('role') === false && (
                            <Grid item xs={12}>
                                <TextField
                                    label="Role"
                                    size="small"
                                    fullWidth
                                    value={user.role}
                                    className={classes.selectLabelGreyDiff}
                                    InputLabelProps={{shrink: true}}
                                    disabled
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}
        </Box>
    );
});
